// eslint-disable-next-line import/no-cycle
import bffApi, {BffResponse} from '@client/bffApi';
import {CloudConvertService, Document, SaveWorkflowDocumentResponse} from '@simpleinvest/ms-cloud-convert-sdk';
import {
  AccountRequest,
  AccountRequestClientCompletions,
  AccountRequestClientCompletionsOptions,
  AccountStatus,
  Beneficiary,
  ClientIncomeIntervals,
  ClientNetWorths,
  CustodianService,
  EmploymentData,
  GetCustodianAccountsStatusByAccountRequestIdOptions,
  GetEmploymentDataOptions,
  InvestmentProfileRecord,
  InvestmentProfileRecordUpdateFields,
  KycDocumentStatus,
  SearchAccountRequestOptions,
  TrustedContact,
  UpdateAccountRequestOptions,
  UpdateAccountRequestTypeOptions,
  UploadKycDocumentOptions,
} from '@simpleinvest/ms-custodian-sdk';
import {ClientService, AccountDetailResponse, ClientPendingAccountsOptions} from '@simpleinvest/ms-client-sdk';
import {apiKey, getLogger} from '@client/services/sdk';
// eslint-disable-next-line import/no-cycle
import {
  requestInterceptorFulfilledHandler,
  requestInterceptorRejectedHandler,
  responseInterceptorFulfilledHandler,
  responseInterceptorRejectedHandler,
} from '@client/interceptors';
import {getUser} from '@simpleinvest/front-end-auth';
import {Custodian, MicroserviceError, SourcePlatform} from '@simpleinvest/ms-core-sdk';
import {AxiosResponse} from 'axios';
import {TrustedContactResponse} from './response/ServiceResponse';

// github.com/RobustWealth/client-bff/blob/main/src/get-active-account-requests/account-requests-transformer.ts#L14
export interface FaiDetails {
  uploaded_file_name: string;
  entity: string;
}

export interface ModifiedAccountRequest {
  account_type?: number;
  cdo_initiated: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  batch_ID?: number;
  rep_code: string;
  account_name: string;
  account_type_name?: string;
  goal_id?: number;
  rollover_ira: number;
  request_initiated_from: string;
  request_id: number;
  cust_id: number;
  status: number;
  account_number?: string;
  bene_cc: number;
  no_bene_selected: number;
  no_trusted_contact: number;
  acc_type_cc: number;
  diff_mail_add: number;
  per_aff_cc: number;
  per_aff_spf: number;
  per_aff_ptc: number;
  per_aff_fai: number;
  client_is_cc: number;
  faiDetails?: FaiDetails;
  beneficiaries?: Beneficiary[];
  trustedContact?: TrustedContact;
}

export default {
  getCloudConvertServiceInstance(): CloudConvertService {
    return new CloudConvertService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger: getLogger(),
    });
  },

  getCustodianServiceInstance({logger = getLogger()} = {}): CustodianService {
    return new CustodianService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger,
    });
  },

  getClientServiceInstance({logger = getLogger()} = {}): ClientService {
    return new ClientService({
      apiKey,
      sourceInfo: {platform: SourcePlatform.ClientPlatform},
      interceptors: {
        requestInterceptor: requestInterceptorFulfilledHandler,
        requestRejectedInterceptor: requestInterceptorRejectedHandler,
        responseInterceptor: responseInterceptorFulfilledHandler,
        responseRejectedInterceptor: responseInterceptorRejectedHandler,
      },
      logger,
    });
  },

  async getAccountRequests(options: SearchAccountRequestOptions): Promise<AccountRequest[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.searchAccountRequest(options);
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateAccountRequest(requestBody: any): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();
    const accountRequestBody = {requestBody};

    return service.addAccount(accountRequestBody);
  },

  async updateAccountRequestType(updateOptions: UpdateAccountRequestTypeOptions): Promise<AccountRequest> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.updateAccountType(updateOptions);
  },

  async deleteAccountRequest(accountRequestId: number): Promise<AccountRequest> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    const {userId} = await getUser();

    return service.updateAccountRequest({
      accountRequestId,
      clUserId: userId,
      // 9 === 'CANCELLED' (MS accepts an integer for accountStatus)
      request: {accountStatus: 9, accountClosed: true},
    });
  },

  async getInvestmentProfile(): Promise<InvestmentProfileRecord> {
    const logger = getLogger();
    const existingLogError = logger.error;

    // Intercept the call to `logger.error`, and if it's a 404, just log it as info rather than logging as an exception.
    // We expect a 404 here before an investment profile is created, and so we don't want to be notified in those cases.
    logger.error = (error: MicroserviceError) =>
      error.microserviceStatusCode === 404 ? logger.info(error.microserviceResponseMessage) : existingLogError(error);

    const service: CustodianService = await this.getCustodianServiceInstance({logger});

    const {userId} = await getUser();

    return service.getInvestmentProfileRecord({
      clUserId: Number(userId),
    });
  },

  async updateInvestmentProfile(payload: InvestmentProfileRecordUpdateFields): Promise<InvestmentProfileRecord> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    const {userId} = await getUser();

    return service.updateInvestmentProfileRecord({
      clUserId: Number(userId),
      payload,
    });
  },

  async updateAccountRequestCompletions(
    options: AccountRequestClientCompletionsOptions
  ): Promise<AccountRequestClientCompletions> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.updateAccountRequestCompletions(options);
  },

  async removeTrustedContact(accountRequestId: number): Promise<unknown> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.removeTrustedContact({accountRequestId});
  },

  async getPending(options: ClientPendingAccountsOptions): Promise<AccountDetailResponse[]> {
    const service = await this.getClientServiceInstance();

    return service.getClientPendingAccounts(options);
  },

  async find(accountRequestId: number): Promise<AccountRequest> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getAccountRequest({accountRequestId});
  },

  async getNetWorthIntervals(custodian: Custodian): Promise<ClientNetWorths[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getClientNetWorths({custodian});
  },

  async getIncomeIntervals(custodian: Custodian): Promise<ClientIncomeIntervals[]> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getClientIncomeIntervals({custodian});
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getAccountOpeningTerms({clUserId, accountRequestId}): Promise<any> {
    const service: CloudConvertService = await this.getCloudConvertServiceInstance();

    return service.getAccountOpeningPdf({clUserId, accountRequestId});
  },

  async getAdvisorTerms({riaId, clUserId, useSegmentWorkflowId}): Promise<string> {
    const service: CloudConvertService = await this.getCloudConvertServiceInstance();

    return service.getWorkflowPreview({
      riaId,
      clUserId,
      useSegmentWorkflowId,
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getIraDepositTerms({clUserId, accountRequestId}): Promise<any> {
    const service: CloudConvertService = await this.getCloudConvertServiceInstance();

    return service.getIraDepositFormPreview({clUserId, accountRequestIdList: accountRequestId});
  },

  async getElectronicDeliveryTerms(): Promise<string> {
    const path = `/application/electronic-delivery-terms`;

    const {data} = await bffApi.getAxiosInstance().get<BffResponse<Document>>(`${bffApi.getBaseUrl(true)}${path}`);

    return data.data.documentBase64;
  },

  async submitAccountOpeningRequest({
    clUserId,
    accountRequestId,
    custodian,
    riaId,
    userTimezone,
  }): Promise<AxiosResponse<BffResponse<AccountStatus>>> {
    const result = await bffApi
      .getAxiosInstance()
      .post<BffResponse<AccountStatus>>(`${bffApi.getBaseUrl(true)}/accounts/submit-request`, {
        clUserId: Number(clUserId),
        accountRequestId: Number(accountRequestId),
        custodian,
        riaId: Number(riaId),
        userTimezone,
      });

    return result;
  },

  async saveAdvisorTermsToDocVault({
    clUserId,
    riaId,
    useUserWorkflowId,
    useSegmentWorkflowId,
  }): Promise<BffResponse<SaveWorkflowDocumentResponse>> {
    const path = `/firm-profile/firms/${riaId}/clients/${clUserId}/workflow`;

    const workflowDocumentResponse = await bffApi
      .getAxiosInstance()
      .post<BffResponse<SaveWorkflowDocumentResponse>>(`${bffApi.getBaseUrl(true)}${path}`, {
        useWorkflow: {useSegmentWorkflowId, useUserWorkflowId},
      });

    return workflowDocumentResponse.data;
  },

  async saveTrustedContact({clUserId, accountsTrustedContact}): Promise<BffResponse<TrustedContactResponse[]>> {
    const {data} = await bffApi
      .getAxiosInstance()
      .put<BffResponse<TrustedContactResponse[]>>(`${bffApi.getBaseUrl(true)}/clients/${clUserId}/trusted-contact`, {
        clUserId: Number(clUserId),
        accountsTrustedContact,
      });

    return data;
  },

  async getActiveAccountRequests({custodian}: {custodian: Custodian}): Promise<ModifiedAccountRequest[]> {
    const {userId} = await getUser();
    const path = `/accounts/requests/active?clUserId=${userId}&custodian=${custodian}`;

    const {data} = await bffApi
      .getAxiosInstance()
      .get<BffResponse<ModifiedAccountRequest[]>>(`${bffApi.getBaseUrl(true)}${path}`);

    return data.data;
  },

  async getCustodianAccountsStatusByAccountRequestId(
    options: GetCustodianAccountsStatusByAccountRequestIdOptions
  ): Promise<AccountStatus> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getCustodianAccountsStatusByAccountRequestId(options);
  },
  async getEmploymentData(options: GetEmploymentDataOptions): Promise<EmploymentData> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.getEmploymentData(options);
  },
  async uploadKycDocument(options: UploadKycDocumentOptions): Promise<KycDocumentStatus> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.uploadKycDocument(options);
  },
  async setAccountRequestStatusToFinishReview(payload: UpdateAccountRequestOptions): Promise<AccountRequest> {
    const service: CustodianService = await this.getCustodianServiceInstance();

    return service.updateAccountRequest(payload);
  },
  async submitAccountOpeningAdvisorInitiated({
    clUserId,
    custodian,
    accountRequestId,
    userTimezone,
  }): Promise<AxiosResponse<BffResponse<unknown>>> {
    const result = await bffApi
      .getAxiosInstance()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .post<BffResponse<any>>(`${bffApi.getBaseUrl(true)}/accounts/advisor-initiated/submit-request`, {
        clUserId: Number(clUserId),
        custodian,
        accountRequestId,
        userTimezone,
      });

    return result;
  },
};
